import { Big } from 'big.js';
import {
    AbstractDeviceConsumablePricingItem,
    AbstractDevicePricingItem,
    Article,
    ConsumablePricingItem,
    DeviceConsumablePricingItem,
    DeviceConsumableWithSuppPricingItem,
    MathUtils,
    OptionPricingItem,
    PricingItem,
    bigToString,
    parseBig
} from 'quote-domain-core';
import { ArticleService } from '../article/article-service';
import { CorridorService } from '../corridor/corridor-service';
import { singleton } from 'tsyringe';

type AppliedPriceSummary = {
    appliedPriceDevices: Big;
    appliedPriceConsumables: Big;
    appliedPriceOnlyNegociableDevices: Big;
    appliedPriceOnlyNegociableConsumables: Big;
    appliedPriceOnlyNonNegociableDevices: Big;
    appliedPriceOnlyNonNegociableConsumables: Big;
};

@singleton()
export class PricingItemPriceService {
    constructor(
        private readonly articleService: ArticleService,
        private readonly corridorService: CorridorService
    ) {
    }

    public sumDevicesAndConsumablesAppliedPrices(pricingItem: PricingItem, result: AppliedPriceSummary): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.sumIntoDevices(pricingItem.device, pricingItem.userInputs?.quantity, result);
            this.sumIntoConsumables(pricingItem.consumable, pricingItem.userInputs?.consumableQuantity, result);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.sumIntoDevices(pricingItem.device, pricingItem.userInputs?.quantity, result);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.sumIntoConsumables(pricingItem.consumable, pricingItem.userInputs?.consumableQuantity, result);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.sumIntoDevices(pricingItem.option, pricingItem.userInputs?.quantity, result);
        }
    }

    private sumIntoDevices(article: Article, quantity: any, result: AppliedPriceSummary): void {
        if (article?.appliedPrice && quantity) {
            result.appliedPriceDevices = this.articleService.sumAppliedPrices(article, result.appliedPriceDevices, parseBig(quantity));
            if (this.articleService.isFlat(article)) {
                result.appliedPriceOnlyNonNegociableDevices = this.articleService.sumAppliedPrices(article, result.appliedPriceOnlyNonNegociableDevices, parseBig(quantity));
            } else {
                result.appliedPriceOnlyNegociableDevices = this.articleService.sumAppliedPrices(article, result.appliedPriceOnlyNegociableDevices, parseBig(quantity));
            }
        }
    }

    private sumIntoConsumables(article: Article, quantity: any, result: AppliedPriceSummary): void {
        if (article?.appliedPrice && quantity) {
            result.appliedPriceConsumables = this.articleService.sumAppliedPrices(article, result.appliedPriceConsumables, parseBig(quantity));
            if (this.articleService.isFlat(article)) {
                result.appliedPriceOnlyNonNegociableConsumables = this.articleService.sumAppliedPrices(article, result.appliedPriceOnlyNonNegociableConsumables, parseBig(quantity));
            } else {
                result.appliedPriceOnlyNegociableConsumables = this.articleService.sumAppliedPrices(article, result.appliedPriceOnlyNegociableConsumables, parseBig(quantity));
            }
        }
    }

    public sumAppliedPrices(pricingItem: PricingItem, result: { appliedPriceDevices: Big, appliedPriceConsumables: Big }): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            if (pricingItem.device?.appliedPrice && pricingItem.userInputs?.quantity) {
                result.appliedPriceDevices = this.articleService.sumAppliedPrices(pricingItem.device, result.appliedPriceDevices, parseBig(pricingItem.userInputs?.quantity));
            }
            if (pricingItem.consumable?.appliedPrice && pricingItem.userInputs?.consumableQuantity) {
                result.appliedPriceConsumables = this.articleService.sumAppliedPrices(pricingItem.consumable, result.appliedPriceConsumables, parseBig(pricingItem.userInputs?.consumableQuantity));
            }
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            if (pricingItem.device?.appliedPrice && pricingItem.userInputs?.quantity) {
                result.appliedPriceDevices = this.articleService.sumAppliedPrices(pricingItem.device, result.appliedPriceDevices, parseBig(pricingItem.userInputs?.quantity));
            }
        } else if (pricingItem instanceof ConsumablePricingItem) {
            if (pricingItem.consumable?.appliedPrice && pricingItem.userInputs?.consumableQuantity) {
                result.appliedPriceConsumables = this.articleService.sumAppliedPrices(pricingItem.consumable, result.appliedPriceConsumables, parseBig(pricingItem.userInputs?.consumableQuantity));
            }
        } else if (pricingItem instanceof OptionPricingItem) {
            if (pricingItem.option?.appliedPrice && pricingItem.userInputs?.quantity) {
                result.appliedPriceDevices = this.articleService.sumAppliedPrices(pricingItem.option, result.appliedPriceDevices, parseBig(pricingItem.userInputs?.quantity));
            }
        }
    }

    public updateLastAppliedPriceState(pricingItem: PricingItem): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.updateLastAppliedPriceState(pricingItem.device);
            this.articleService.updateLastAppliedPriceState(pricingItem.consumable);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            if (pricingItem.device) this.articleService.updateLastAppliedPriceState(pricingItem.device);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.updateLastAppliedPriceState(pricingItem.consumable);
        } else if (pricingItem instanceof OptionPricingItem) {
            if (pricingItem.option) this.articleService.updateLastAppliedPriceState(pricingItem.option);
        }
    }

    public calculateDistance(pricingItem: PricingItem, nonNegociableArticleColor: string): void {
        if (pricingItem instanceof DeviceConsumableWithSuppPricingItem) {
            if (pricingItem.deviceConsumableCorridor) {
                const distance = bigToString(this.corridorService.getDistanceByAppliedPrice(pricingItem.deviceConsumableCorridor, pricingItem.deviceConsumableAppliedPrice));
                const matchedColor = this.corridorService.getMatchedColorByappliedPrice(pricingItem.deviceConsumableCorridor, pricingItem.deviceConsumableAppliedPrice, nonNegociableArticleColor);
                this.articleService.updateDistanceAndAppliedPriceColor(pricingItem.consumable, distance, matchedColor);
                this.articleService.updateDistanceAndAppliedPriceColor(pricingItem.device, distance, matchedColor);
            } else {
                this.articleService.calculateDistance(pricingItem.consumable, nonNegociableArticleColor);
                this.articleService.calculateDistance(pricingItem.device, nonNegociableArticleColor);
            }
        } else if (pricingItem instanceof DeviceConsumablePricingItem) {
            this.articleService.calculateDistance(pricingItem.consumable, nonNegociableArticleColor);
            this.articleService.calculateDistance(pricingItem.device, nonNegociableArticleColor);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            if (pricingItem.device) this.articleService.calculateDistance(pricingItem.device, nonNegociableArticleColor);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.calculateDistance(pricingItem.consumable, nonNegociableArticleColor);
        } else if (pricingItem instanceof OptionPricingItem) {
            if (pricingItem.option) this.articleService.calculateDistance(pricingItem.option, nonNegociableArticleColor);
        }
    }

    public calculateDiscount(pricingItem: PricingItem): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.calculateDiscount(pricingItem.consumable);
            this.articleService.calculateDiscount(pricingItem.device);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            if (pricingItem.device) this.articleService.calculateDiscount(pricingItem.device);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            if (pricingItem.consumable) this.articleService.calculateDiscount(pricingItem.consumable);
        } else if (pricingItem instanceof OptionPricingItem) {
            if (pricingItem.option) this.articleService.calculateDiscount(pricingItem.option);
        }
    }

    public calculateTotalAppliedPrice(pricingItem: PricingItem): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            let totalAppliedPrice = parseBig(pricingItem.device.appliedPrice).mul(parseBig(pricingItem.userInputs.quantity));
            totalAppliedPrice = totalAppliedPrice.add(parseBig(pricingItem.consumable.appliedPrice).mul(parseBig(pricingItem.userInputs.consumableQuantity)));
            Object.assign(pricingItem, { totalAppliedPrice: totalAppliedPrice ? bigToString(totalAppliedPrice) : pricingItem.totalAppliedPrice });
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            const totalAppliedPrice = parseBig(pricingItem.device.appliedPrice).mul(parseBig(pricingItem.userInputs.quantity));
            Object.assign(pricingItem, { totalAppliedPrice: totalAppliedPrice ? bigToString(totalAppliedPrice) : pricingItem.totalAppliedPrice });
        } else if (pricingItem instanceof ConsumablePricingItem) {
            const totalAppliedPrice = parseBig(pricingItem.consumable.appliedPrice).mul(parseBig(pricingItem.userInputs.consumableQuantity));
            Object.assign(pricingItem, { totalAppliedPrice: totalAppliedPrice ? bigToString(totalAppliedPrice) : pricingItem.totalAppliedPrice });
        } else if (pricingItem instanceof OptionPricingItem) {
            const totalAppliedPrice = parseBig(pricingItem.option.appliedPrice).mul(parseBig(pricingItem.userInputs.quantity));
            Object.assign(pricingItem, { totalAppliedPrice: totalAppliedPrice ? bigToString(totalAppliedPrice) : pricingItem.totalAppliedPrice });
        }
    }

    public getAdditionalStockAmount(pricingItem: PricingItem): Big {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            const extraPrice = parseBig(pricingItem.consumableExtraPrice);
            const consumableAppliedPrice = parseBig(pricingItem.consumable.appliedPrice);
            const bufferStock = parseBig(pricingItem.userInputs['bufferStock']);
            const franchise = parseBig(pricingItem.franchise);
            const bufferStockAndExtraPrice = bufferStock.mul(extraPrice);
            let billedBufferStock = new Big(0);
            if (!MathUtils.bigIsNullOrZero(consumableAppliedPrice) && !MathUtils.bigIsNullOrZero(franchise) && !!pricingItem.userInputs.quantity) {
                const initialStock = franchise.mul(pricingItem.userInputs.quantity).minus(pricingItem.userInputs.consumableQuantity);
                const initialStockFinal = initialStock.gt(0) ? initialStock : new Big(0);
                billedBufferStock = initialStockFinal.mul(consumableAppliedPrice);
            }
            return bufferStockAndExtraPrice.plus(billedBufferStock);
        }
        if (pricingItem instanceof ConsumablePricingItem) {
            const extraPrice = parseBig(pricingItem.consumableExtraPrice);
            const bufferStock = pricingItem.userInputs['bufferStock'];
            return parseBig(bufferStock).mul(extraPrice);
        }
        return new Big(0);
    }

    public isNeutralCorridor(pricingItem: PricingItem): boolean {
        if (pricingItem instanceof DeviceConsumablePricingItem) {
            return false;
        }
        if (pricingItem instanceof DeviceConsumableWithSuppPricingItem) {
            return (!pricingItem.device.corridor || pricingItem.device.corridor.isNeutral) && (!pricingItem.consumable.corridor || pricingItem.consumable.corridor.isNeutral);
        }
        if (pricingItem instanceof AbstractDevicePricingItem) {
            return !pricingItem.device?.corridor || pricingItem.device.corridor.isNeutral;
        }
        if (pricingItem instanceof ConsumablePricingItem) {
            return !pricingItem.consumable?.corridor || pricingItem.consumable.corridor.isNeutral;
        }
        if (pricingItem instanceof OptionPricingItem) {
            return !pricingItem.option?.corridor || pricingItem.option.corridor.isNeutral;
        }
        return true;
    }
}
